import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, UserPlus, Check, ChevronRight, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api } from "@/lib/api";

const input = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";
const err = (e, f) => e?.response?.data?.detail || f;

// One button on the checklist row. The owner's words: "the buttons weren't easily visible, I had
// to go into settings or checklist or where. I didn't see what the invite said and I had no idea
// who it went to." So: one control, one panel, the recipient and the exact words on screen.
export const WhoPill = ({ row, onClick }) => {
  const waiting = row.waiting_on_office;
  const who = row.holder || "";
  return (
    <button onClick={onClick} data-testid={`who-pill-${row.id}`}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
        waiting ? "border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400"
          : who ? "border-sand-300 bg-white text-ink hover:border-sand-400"
            : "border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400"}`}>
      {waiting ? <Clock className="h-3 w-3 shrink-0" /> : null}
      <span className="truncate">
        {waiting ? `Waiting on ${row.handoff?.asked?.[0] || "the office"}`
          : who ? `Who's doing this: ${who}` : "Who's doing this: nobody yet"}
      </span>
      <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
    </button>
  );
};

const Row = ({ on, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex w-full items-center gap-2 rounded-xl border px-3.5 py-2.5 text-left text-sm font-semibold ${
      on ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-sand-200 bg-white hover:border-sand-300"}`}>
    {on ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
    <span className="min-w-0 flex-1 truncate">{children}</span>
  </button>
);

export const WhoDialog = ({ tenantId, row, onClose, onSaved }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("");
  const [fresh, setFresh] = useState({ name: "", phone: "" });
  const [office, setOffice] = useState({ name: "", phone: "" });
  const [showSent, setShowSent] = useState(false);

  const load = () => api.get(`/tenants/${tenantId}/checklists/${row.id}/handoff`)
    .then((r) => setD(r.data)).catch((e) => toast.error(err(e, "Couldn't load this")));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [row.id]);

  const done = (msg) => { toast.success(msg); onSaved?.(); onClose(); };

  const assign = async (scope) => {
    setBusy(true);
    try {
      const body = pick === "new" ? { name: fresh.name.trim(), phone: fresh.phone.trim(), scope }
        : { contact_id: pick, scope };
      const { data } = await api.post(`/tenants/${tenantId}/checklists/${row.id}/assign`, body);
      done(`${data.holder} is on ${d.name} — their link was texted to ${data.sent_to}`);
    } catch (e) { toast.error(err(e, "Couldn't do that")); } finally { setBusy(false); }
  };

  const ask = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/checklists/${row.id}/ask-office`);
      done(`Sent to ${(data.asked || []).join(", ")} — you'll hear as soon as they pick somebody`);
    } catch (e) { toast.error(err(e, "Couldn't send")); } finally { setBusy(false); }
  };

  const addOffice = async () => {
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/checklists/office`,
        { vendor_id: d.vendor_id, name: office.name.trim(), phone: office.phone.trim() });
      toast.success(`${office.name.trim()} is ${d.vendor}'s office contact`);
      setOffice({ name: "", phone: "" });
      await load();
    } catch (e) { toast.error(err(e, "Couldn't add them")); } finally { setBusy(false); }
  };

  const ready = pick === "new" ? !!(fresh.name.trim() && fresh.phone.trim()) : !!pick;
  const waiting = d?.handoff?.state === "waiting";

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="who-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Who's doing {row.name}?</DialogTitle>
        </DialogHeader>

        {!d && <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>}

        {d && !d.vendor_id && (
          <p className="text-sm text-muted-foreground">Put a company on this checklist first, then you can say who at that company is doing it.</p>
        )}

        {d && d.vendor_id && (
          <div className="space-y-5">
            <p className="text-sm" data-testid="who-current">
              {d.holder
                ? <>On it now: <b className="font-semibold">{d.holder.name}</b> at {d.vendor}</>
                : <><b className="font-semibold text-amber-800">Nobody is named</b> — whoever at {d.vendor} gets the text can do it</>}
            </p>

            {d.last_on_site && d.last_on_site.toLowerCase() !== (d.holder?.name || "").toLowerCase() && (
              <p className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3.5 text-sm text-amber-900"
                data-testid="who-on-site">
                <b className="font-semibold">{d.last_on_site}</b> is the one who has actually been turning up.
                Pick them below and the list, the texts and the reminders go to them
                {d.holder ? ` instead of ${d.holder.name}` : ""}.
              </p>
            )}

            {waiting && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3.5" data-testid="who-waiting">
                <p className="text-sm font-semibold text-amber-900">
                  Waiting on {(d.handoff.asked || []).join(", ") || "the office"} since {d.handoff.at}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-3 text-xs font-semibold">
                  <button onClick={() => setShowSent(!showSent)} data-testid="who-see-sent"
                    className="text-amber-900 underline underline-offset-2">
                    {showSent ? "Hide what we sent" : "See what we sent"}
                  </button>
                  <button onClick={ask} disabled={busy} data-testid="who-remind"
                    className="text-redline-600 underline underline-offset-2 disabled:opacity-50">Send it again</button>
                </div>
                {showSent && (d.handoff.sent || []).map((s, i) => (
                  <div key={i} className="mt-2 rounded-xl bg-white/80 p-3" data-testid={`who-sent-${i}`}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink/40">
                      To {s.name} · {s.to} · {s.at}
                    </div>
                    <p className="mt-1 whitespace-pre-line text-xs text-ink/70">{s.body}</p>
                  </div>
                ))}
              </div>
            )}

            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-ink/40">Pick somebody now</div>
              <p className="mt-1 text-xs text-muted-foreground">They get their own link by text straight away. Nothing goes to the company.</p>
              <div className="mt-2 space-y-1.5">
                {(d.crew || []).map((c) => (
                  <Row key={c.contact_id} on={pick === c.contact_id} testid={`who-crew-${c.contact_id}`}
                    onClick={() => setPick(c.contact_id)}>
                    {c.name}{c.office ? " · office" : ""}{c.on_site ? " · on site last time" : ""}
                  </Row>
                ))}
                <Row on={pick === "new"} testid="who-new" onClick={() => setPick("new")}>
                  <span className="inline-flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Somebody new</span>
                </Row>
                {pick === "new" && (
                  <div className="grid gap-2 pt-1 sm:grid-cols-2">
                    <input className={input} placeholder="Their name" data-testid="who-new-name"
                      value={fresh.name} onChange={(e) => setFresh({ ...fresh, name: e.target.value })} />
                    <input className={input} placeholder="Their mobile" data-testid="who-new-phone"
                      value={fresh.phone} onChange={(e) => setFresh({ ...fresh, phone: e.target.value })} />
                  </div>
                )}
              </div>
              {pick && (
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <button onClick={() => assign("always")} disabled={busy || !ready} data-testid="who-assign-always"
                    className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Put them on it
                  </button>
                  <button onClick={() => assign("visit")} disabled={busy || !ready} data-testid="who-assign-visit"
                    className="rounded-full border border-sand-300 px-4 py-2 text-sm font-semibold hover:border-sand-400 disabled:opacity-50">
                    Just the next visit
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-sand-100 pt-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-ink/40">
                Or ask {d.vendor} to pick
              </div>
              {d.needs_office ? (
                <>
                  <p className="mt-1 text-xs text-amber-800" data-testid="who-needs-office">
                    {d.vendor} has nobody in the office on file, so there's nobody to ask. Add whoever answers
                    the phone there — employees come and go, that number doesn't.
                  </p>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <input className={input} placeholder="Their name" data-testid="who-office-name"
                      value={office.name} onChange={(e) => setOffice({ ...office, name: e.target.value })} />
                    <input className={input} placeholder="Their mobile" data-testid="who-office-phone"
                      value={office.phone} onChange={(e) => setOffice({ ...office, phone: e.target.value })} />
                  </div>
                  <button onClick={addOffice} data-testid="who-office-add"
                    disabled={busy || !office.name.trim() || !office.phone.trim()}
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-redline-500 px-4 py-2 text-sm font-semibold text-white hover:bg-redline-600 disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                    Add them
                  </button>
                </>
              ) : (
                <>
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="who-office-to">
                    This text goes to <b className="font-semibold text-ink">{(d.office[0] || {}).name}</b>
                    {d.office.length > 1 ? ` and ${d.office.length - 1} more` : ""} · {(d.office[0] || {}).to}
                  </p>
                  <div className="mt-2 rounded-2xl border border-sand-200 bg-sand-50/70 p-3" data-testid="who-preview">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink/35">They'll get exactly this</div>
                    <p className="mt-1 whitespace-pre-line text-xs text-ink/70">{d.preview}</p>
                  </div>
                  <button onClick={ask} disabled={busy} data-testid="who-ask-office"
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-redline-500 px-4 py-2 text-sm font-semibold text-white hover:bg-redline-600 disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Send this to {(d.office[0] || {}).name?.split(" ")[0] || "them"}
                  </button>
                </>
              )}
            </div>

            <p className="text-xs text-muted-foreground" data-testid="who-footer-note">
              Whoever ends up on it, you get a text and a note in the app — and this list keeps running
              with {d.holder ? d.holder.name : "whoever gets the text"} until somebody else is on it.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
